import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { ModalContextProvider, useModalContext } from 'utils/contexts/modal';
import { selectUserProjectDefaultSenderEmail } from 'core/userContext';
import { PageHeader } from 'ui/pageHeader';
import { WarningBanner } from 'ui/banner';
import { FlexSpacer } from 'ui/flexSpacer';
import { PrimaryButton, SecondaryButton } from 'ui/button';
import { Link } from 'ui/links';
import { Body2 } from 'ui/theme';
import { TabsGroup, IndexTabTrigger, IndexTabsList, TabsContent } from 'ui/tabsGroup';
import { AllSendersModals } from './AllSendersModals';
import { fetchSenders } from '../actions';
import {
  selectAuthedEmailSendersTableData,
  selectBannersState,
  selectSmsSendersTableData,
} from '../selectors';
import { AuthedEmailsTable } from './AuthedEmailsTable';
import { SmsSendersTable } from './SmsSendersTable';
import type { AllSendersModalState } from '../types';

const Senders = () => {
  const dispatch = useDispatch();
  const authedEmails = useSelector(selectAuthedEmailSendersTableData);
  const smsSendersTableData = useSelector(selectSmsSendersTableData);
  const defaultSender = useSelector(selectUserProjectDefaultSenderEmail);
  const { showUnverifiedEmailsBanner, showSmsSendersBanner, smsMessageTypesBanner } =
    useSelector(selectBannersState);
  const { showModal } = useModalContext<AllSendersModalState>();

  useEffect(() => {
    dispatch(fetchSenders());
  }, [dispatch]);

  return (
    <>
      <PageHeader
        pageActions={
          <>
            <SecondaryButton
              data-test="senders-set-new-default-sender-modal"
              onClick={() =>
                showModal({ action: 'authedEmailSetDefaultSenderModal', defaultSender })
              }
            >
              Set new default email sender
            </SecondaryButton>
            <PrimaryButton
              data-test="senders-create-new-sender-modal"
              onClick={() => showModal({ action: 'authedEmailCreateModal' })}
            >
              Create new email sender
            </PrimaryButton>
          </>
        }
        pageSubSection={
          <Body2>
            After adding a sender, you will receive a verification email from Amazon if you have not
            set up&nbsp;
            <Link href="/settings/dnsSetup" useReactRouter>
              Domain DNS
            </Link>
            .
          </Body2>
        }
      >
        Senders
      </PageHeader>
      <FlexSpacer flexDirection="column" alignItems="stretch">
        {showUnverifiedEmailsBanner && (
          <WarningBanner data-test="senders-verified-sender-warning-banner">
            A verified sender is required to create a template or campaign.
          </WarningBanner>
        )}
        {showSmsSendersBanner && (
          <WarningBanner data-test="senders-sms-sender-warning-banner">
            You must have at least one short code or messaging service configured to send SMS
            messages. Please contact your account manager for more information.
          </WarningBanner>
        )}
        {smsMessageTypesBanner && (
          <WarningBanner data-test="senders-sms-message-type-banner">
            You must create an SMS message type to send SMS. Create one{' '}
            <Link href="/settings/channels/sms" useReactRouter>
              here
            </Link>
            .
          </WarningBanner>
        )}
        <TabsGroup defaultValue="email">
          <FlexSpacer flexDirection="column" alignItems="stretch">
            <IndexTabsList>
              <IndexTabTrigger data-test="senders-email-senders-tab-trigger" value="email">
                Email
              </IndexTabTrigger>
              <IndexTabTrigger data-test="senders-sms-senders-tab-trigger" value="phone">
                SMS
              </IndexTabTrigger>
            </IndexTabsList>
            <TabsContent data-test="senders-email-senders-tab-content" value="email">
              <AuthedEmailsTable authedEmails={authedEmails} />
            </TabsContent>
            <TabsContent data-test="senders-sms-senders-tab-content" value="phone">
              <SmsSendersTable smsSenders={smsSendersTableData} />
            </TabsContent>
          </FlexSpacer>
        </TabsGroup>
      </FlexSpacer>
      <AllSendersModals />
    </>
  );
};

export const SendersPage = () => {
  return (
    <ModalContextProvider>
      <Senders />
    </ModalContextProvider>
  );
};
